//! Order book in stile Polymarket. Layout 3 colonne PREZZO|AZIONI|TOTALE,
//! ask in alto (rosso) + pill "Vendite" laterale, bid in basso (verde) + pill "Acquisti".
//! Mid-row con "Ultimo" + "Spread".

import { parseDecimal } from "@/components/atoms/format";
import type { components } from "@/api/schema.gen";

type OrderBookView = components["schemas"]["OrderBookView"];

interface Props {
  book: OrderBookView | null;
}

interface Level {
  price: number;
  size: number;
  total: number;
}

const GRID = "60px minmax(0,3fr) 110px 110px 110px";

export function OrderBook({ book }: Props) {
  if (!book) {
    return (
      <div className="card p-4">
        <div className="skel mb-2 h-3 w-1/2" />
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="skel mb-1 h-5 w-full" />
        ))}
      </div>
    );
  }

  // Asks (sell side, prezzi più alti, rosso). Già ordinati ASC dal backend;
  // li mostriamo top→bottom dal più alto al più basso (più lontano dal mid → più vicino).
  const asksAsc = book.asks
    .map((a) => ({
      price: parseDecimal(a.price),
      size: parseDecimal(a.size),
      total: parseDecimal(a.price) * parseDecimal(a.size),
    }))
    .filter((a) => a.size > 0)
    .slice(0, 6)
    .reverse();

  // Bids (buy side, prezzi più bassi del mid, verde). Top = best bid.
  const bids: Level[] = book.bids
    .map((b) => ({
      price: parseDecimal(b.price),
      size: parseDecimal(b.size),
      total: parseDecimal(b.price) * parseDecimal(b.size),
    }))
    .filter((b) => b.size > 0)
    .slice(0, 6);

  const bestAsk = asksAsc.at(-1)?.price ?? null;
  const bestBid = bids[0]?.price ?? null;
  const mid =
    bestAsk !== null && bestBid !== null ? (bestAsk + bestBid) / 2 : null;
  const spread = bestAsk !== null && bestBid !== null ? bestAsk - bestBid : null;

  const maxSize = Math.max(
    1,
    ...asksAsc.map((a) => a.size),
    ...bids.map((b) => b.size),
  );

  return (
    <div className="card overflow-hidden p-0">
      {/* Header */}
      <div
        className="grid items-center gap-3 border-b border-line bg-bg-2 px-4 py-2 text-[10.5px] uppercase tracking-wider text-muted"
        style={{ gridTemplateColumns: GRID }}
      >
        <span>TRADE SÌ</span>
        <span />
        <span className="text-right">Prezzo</span>
        <span className="text-right">Azioni</span>
        <span className="text-right">Totale</span>
      </div>

      {/* Asks (rosso) */}
      <div className="relative">
        {asksAsc.length > 0 && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 z-10">
            <span className="inline-flex items-center rounded-[6px] bg-no px-2 py-[3px] text-[10px] font-semibold uppercase tracking-wider text-white">
              Vendite
            </span>
          </div>
        )}
        {asksAsc.map((a, i) => (
          <Row key={`a-${i}`} level={a} side="ask" max={maxSize} />
        ))}
      </div>

      {/* Mid row */}
      {mid !== null && (
        <div
          className="grid items-center gap-3 border-y border-line bg-bg-3 px-4 py-2 text-[11.5px]"
          style={{ gridTemplateColumns: GRID }}
        >
          <span />
          <span className="text-muted">
            Ultimo:{" "}
            <span className="font-mono text-text-2">
              {(mid * 100).toFixed(0)}¢
            </span>
          </span>
          <span />
          <span />
          <span className="text-right text-muted">
            Spread:{" "}
            <span className="font-mono text-text-2">
              {((spread ?? 0) * 100).toFixed(1)}¢
            </span>
          </span>
        </div>
      )}

      {/* Bids (verde) */}
      <div className="relative">
        {bids.length > 0 && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 z-10">
            <span className="inline-flex items-center rounded-[6px] bg-yes px-2 py-[3px] text-[10px] font-semibold uppercase tracking-wider text-[#04140d]">
              Acquisti
            </span>
          </div>
        )}
        {bids.map((b, i) => (
          <Row key={`b-${i}`} level={b} side="bid" max={maxSize} />
        ))}
      </div>

      {asksAsc.length === 0 && bids.length === 0 && (
        <div className="px-4 py-6 text-center text-[12.5px] text-muted">
          Order book vuoto — nessun ordine attivo.
        </div>
      )}
    </div>
  );
}

function Row({
  level,
  side,
  max,
}: {
  level: Level;
  side: "ask" | "bid";
  max: number;
}) {
  const pct = Math.min(100, (level.size / max) * 100);
  const bg = side === "ask" ? "var(--no-soft)" : "var(--yes-soft)";
  const color = side === "ask" ? "var(--no)" : "var(--yes)";
  return (
    <div
      className="relative grid items-center gap-3 px-4 py-[7px] text-[12.5px]"
      style={{ gridTemplateColumns: GRID }}
    >
      <span
        className="absolute right-0 top-0 h-full"
        style={{ width: `${pct}%`, background: bg, opacity: 0.55 }}
      />
      <span /> {/* spazio per la pill laterale */}
      <span />
      <span
        className="relative text-right font-mono font-semibold"
        style={{ color }}
      >
        {(level.price * 100).toFixed(0)}¢
      </span>
      <span className="relative text-right font-mono text-text-2">
        {level.size.toLocaleString("it-IT", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </span>
      <span className="relative text-right font-mono text-muted">
        ${level.total.toLocaleString("it-IT", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </span>
    </div>
  );
}
